import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2, Upload, Play, Star, CheckCircle, AlertCircle, Film,
} from 'lucide-react';
import {
  adminAssignModelToTier,
  adminEstimateTestCost,
  adminPollModelTest,
  adminScoreTestResult,
  adminStartModelTest,
  uploadAdminTestImage,
} from '../../services/motion-tiers';
import { supabase } from '../../services/supabase';
import type {
  AdminModelTestResult,
  AdminModelTestScores,
  AdminMotionModel,
  MotionTierKey,
} from '../../types/motion-tiers';
import { TIER_ASSIGN_OPTIONS } from '../../types/motion-tiers';

interface Banner {
  type: 'success' | 'error';
  message: string;
}

interface ModelTestingTabProps {
  models: AdminMotionModel[];
  onBanner: (banner: Banner) => void;
}

const SCORE_FIELDS: { key: keyof AdminModelTestScores; label: string }[] = [
  { key: 'score_image_preservation', label: 'Image preservation' },
  { key: 'score_architectural_stability', label: 'Architectural stability' },
  { key: 'score_realism', label: 'Realism' },
  { key: 'score_camera_movement', label: 'Camera movement' },
  { key: 'score_prompt_accuracy', label: 'Prompt accuracy' },
  { key: 'score_commercial_usability', label: 'Commercial usability' },
];

const emptyScores = (): AdminModelTestScores => ({
  score_image_preservation: null,
  score_architectural_stability: null,
  score_realism: null,
  score_camera_movement: null,
  score_prompt_accuracy: null,
  score_commercial_usability: null,
});

export default function ModelTestingTab({ models, onBanner }: ModelTestingTabProps) {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<{ url: string; path: string } | null>(null);
  const [prompt, setPrompt] = useState(
    'Smooth cinematic dolly forward through the scene with pronounced natural parallax between foreground furniture, midground architecture and the background. The building, furniture and architectural elements remain completely stable. Camera motion is the primary source of movement. Very subtle natural foliage movement only. No warping, morphing, furniture movement or artificial object animation.'
  );
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);
  const [durationSeconds, setDurationSeconds] = useState(5);
  const [resolution, setResolution] = useState('1080p');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [estimatedCost, setEstimatedCost] = useState<number | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [testId, setTestId] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<string | null>(null);
  const [results, setResults] = useState<AdminModelTestResult[]>([]);
  const [scoreDrafts, setScoreDrafts] = useState<Record<string, AdminModelTestScores>>({});
  const [notesDrafts, setNotesDrafts] = useState<Record<string, string>>({});
  const [savingScoreId, setSavingScoreId] = useState<string | null>(null);
  const [assigningKey, setAssigningKey] = useState<string | null>(null);

  const testableModels = useMemo(
    () => models.filter((m) => m.is_active),
    [models]
  );

  useEffect(() => {
    if (!imageFile) {
      setImagePreview(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    setUploaded(null);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (selectedModelIds.length === 0) {
        setEstimatedCost(null);
        return;
      }
      setEstimating(true);
      const res = await adminEstimateTestCost(selectedModelIds);
      if (cancelled) return;
      setEstimating(false);
      if (!res.success) {
        setEstimatedCost(null);
        return;
      }
      setEstimatedCost(
        typeof res.estimated_total_cost_usd === 'number' ? res.estimated_total_cost_usd : null
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedModelIds]);

  useEffect(() => {
    if (!testId || !polling) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      const res = await adminPollModelTest(testId);
      if (cancelled) return;
      if (!res.success) {
        onBanner({ type: 'error', message: res.error || 'Poll failed' });
        setPolling(false);
        return;
      }
      const nextResults = res.results ?? res.test?.results ?? [];
      setResults(nextResults);
      setTestStatus(res.status ?? res.test?.status ?? null);

      const drafts: Record<string, AdminModelTestScores> = {};
      const notes: Record<string, string> = {};
      for (const r of nextResults) {
        drafts[r.id] = {
          score_image_preservation: r.score_image_preservation ?? null,
          score_architectural_stability: r.score_architectural_stability ?? null,
          score_realism: r.score_realism ?? null,
          score_camera_movement: r.score_camera_movement ?? null,
          score_prompt_accuracy: r.score_prompt_accuracy ?? null,
          score_commercial_usability: r.score_commercial_usability ?? null,
        };
        notes[r.id] = r.rating_notes ?? '';
      }
      setScoreDrafts((prev) => ({ ...drafts, ...prev }));
      setNotesDrafts((prev) => ({ ...notes, ...prev }));

      const done = nextResults.length > 0 && nextResults.every(
        (r) => r.status === 'completed' || r.status === 'failed'
      );
      const terminalTest =
        res.status === 'completed' ||
        res.status === 'failed' ||
        res.status === 'partial' ||
        res.test?.status === 'completed' ||
        res.test?.status === 'failed' ||
        res.test?.status === 'partial';

      if (done || terminalTest) {
        setPolling(false);
        onBanner({ type: 'success', message: 'Model test finished' });
        return;
      }

      timer = setTimeout(tick, 4000);
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [testId, polling, onBanner]);

  const toggleModel = (id: string) => {
    setSelectedModelIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const ensureUploaded = async (): Promise<{ url: string; path: string }> => {
    if (uploaded) return uploaded;
    if (!imageFile) throw new Error('Upload an image first');
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) throw new Error('You must be logged in');
    const result = await uploadAdminTestImage(imageFile, userId);
    setUploaded(result);
    return result;
  };

  const handleStart = async () => {
    setStarting(true);
    try {
      const image = await ensureUploaded();
      const res = await adminStartModelTest({
        imageUrl: image.url,
        imagePath: image.path,
        prompt,
        modelIds: selectedModelIds,
        durationSeconds,
        resolution,
        aspectRatio,
        confirmCost: true,
      });
      if (!res.success || !res.testId) {
        onBanner({ type: 'error', message: res.error || 'Failed to start test' });
        setStarting(false);
        setConfirmOpen(false);
        return;
      }
      setTestId(res.testId);
      setTestStatus('running');
      setResults([]);
      setScoreDrafts({});
      setNotesDrafts({});
      setPolling(true);
      setConfirmOpen(false);
      onBanner({ type: 'success', message: 'Test started — polling results…' });
    } catch (e) {
      onBanner({
        type: 'error',
        message: e instanceof Error ? e.message : 'Failed to start test',
      });
    }
    setStarting(false);
  };

  const saveScores = async (resultId: string) => {
    setSavingScoreId(resultId);
    const scores = scoreDrafts[resultId] ?? emptyScores();
    const res = await adminScoreTestResult(resultId, scores, notesDrafts[resultId] ?? null);
    setSavingScoreId(null);
    if (!res.success) {
      onBanner({ type: 'error', message: res.error || 'Failed to save scores' });
      return;
    }
    onBanner({ type: 'success', message: 'Scores saved' });
  };

  const assignToTier = async (modelId: string, tierKey: MotionTierKey) => {
    const key = `${modelId}:${tierKey}`;
    setAssigningKey(key);
    const res = await adminAssignModelToTier(modelId, tierKey);
    setAssigningKey(null);
    if (!res.success) {
      onBanner({ type: 'error', message: res.error || 'Failed to assign model' });
      return;
    }
    const label = TIER_ASSIGN_OPTIONS.find((t) => t.key === tierKey)?.label ?? tierKey;
    onBanner({
      type: 'success',
      message: `Draft created for ${label}${res.draft_version_id ? ` (${res.draft_version_id.slice(0, 8)}…)` : ''}`,
    });
  };

  const modelName = (modelId: string, fallback?: string | null) => {
    const m = models.find((x) => x.id === modelId);
    return m?.admin_name ?? fallback ?? modelId.slice(0, 8);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Test image</label>
            <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-slate-700 rounded-xl bg-slate-900/50 px-4 py-8 cursor-pointer hover:border-blue-500/50 transition-colors">
              {imagePreview ? (
                <img src={imagePreview} alt="Test" className="max-h-40 rounded-lg object-contain" />
              ) : (
                <>
                  <Upload className="w-6 h-6 text-slate-500" />
                  <span className="text-sm text-slate-400">Upload a property photo</span>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Prompt</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Duration (s)</label>
              <input
                type="number"
                min={1}
                max={15}
                value={durationSeconds}
                onChange={(e) => setDurationSeconds(Number(e.target.value) || 5)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Resolution</label>
              <select
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="720p">720p</option>
                <option value="1080p">1080p</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Aspect</label>
              <select
                value={aspectRatio}
                onChange={(e) => setAspectRatio(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="16:9">16:9</option>
                <option value="9:16">9:16</option>
                <option value="1:1">1:1</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1.5">
            Models ({selectedModelIds.length} selected)
          </label>
          <div className="max-h-72 overflow-y-auto space-y-2 border border-slate-800 rounded-xl p-3 bg-slate-900/40">
            {testableModels.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No active models</p>
            ) : (
              testableModels.map((m) => (
                <label
                  key={m.id}
                  className={`flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                    selectedModelIds.includes(m.id)
                      ? 'border-blue-500/50 bg-blue-950/30'
                      : 'border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedModelIds.includes(m.id)}
                    onChange={() => toggleModel(m.id)}
                    className="mt-1"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-white font-medium">{m.admin_name}</span>
                      {m.testing_only && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-400">
                          testing only
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 font-mono truncate" title={m.endpoint}>
                      {m.endpoint}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Est.{' '}
                      {m.estimated_cost_fixed_usd != null
                        ? `$${Number(m.estimated_cost_fixed_usd).toFixed(2)}`
                        : '—'}
                    </p>
                  </div>
                </label>
              ))
            )}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-slate-300">
              Estimated test cost:{' '}
              {estimating ? (
                <Loader2 className="inline w-3.5 h-3.5 animate-spin text-slate-400" />
              ) : estimatedCost != null ? (
                <span className="font-semibold text-amber-400">${estimatedCost.toFixed(2)}</span>
              ) : (
                <span className="text-slate-500">—</span>
              )}
            </div>
            <button
              type="button"
              disabled={!imageFile || selectedModelIds.length === 0 || !prompt.trim()}
              onClick={() => setConfirmOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Play className="w-4 h-4" />
              Run comparison
            </button>
          </div>
        </div>
      </div>

      {(testId || results.length > 0) && (
        <div className="border-t border-slate-800 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Film className="w-4 h-4 text-blue-400" />
            <h3 className="text-sm font-semibold text-white">Results</h3>
            {polling && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
            {testStatus && (
              <span className="text-xs text-slate-500 capitalize">{testStatus}</span>
            )}
          </div>

          {results.length === 0 ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              Waiting for model outputs…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {results.map((r) => (
                <div
                  key={r.id}
                  className="border border-slate-800 rounded-xl bg-slate-900/50 overflow-hidden"
                >
                  <div className="px-3 py-2 border-b border-slate-800 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate">
                        {modelName(r.model_id, r.model_admin_name)}
                      </p>
                      {r.fal_endpoint && (
                        <p className="text-[10px] text-slate-500 font-mono truncate" title={r.fal_endpoint}>
                          {r.fal_endpoint}
                        </p>
                      )}
                    </div>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                        r.status === 'completed'
                          ? 'bg-green-900/40 text-green-400'
                          : r.status === 'failed'
                            ? 'bg-red-900/40 text-red-400'
                            : 'bg-slate-700/60 text-slate-400'
                      }`}
                    >
                      {r.status}
                    </span>
                  </div>

                  <div className="aspect-video bg-black flex items-center justify-center">
                    {r.video_url ? (
                      <video src={r.video_url} controls className="w-full h-full object-contain" />
                    ) : r.error_message ? (
                      <p className="text-xs text-red-400 px-3 text-center">{r.error_message}</p>
                    ) : (
                      <Loader2 className="w-5 h-5 animate-spin text-slate-600" />
                    )}
                  </div>

                  <div className="p-3 space-y-2">
                    <p className="text-[11px] text-slate-500">
                      Cost:{' '}
                      {r.estimated_cost_usd != null
                        ? `$${Number(r.estimated_cost_usd).toFixed(2)}`
                        : '—'}
                      {r.processing_time_ms != null
                        ? ` · ${(r.processing_time_ms / 1000).toFixed(1)}s`
                        : ''}
                    </p>

                    {SCORE_FIELDS.map((f) => (
                      <div key={f.key} className="flex items-center justify-between gap-2">
                        <span className="text-[11px] text-slate-400">{f.label}</span>
                        <select
                          value={scoreDrafts[r.id]?.[f.key] ?? ''}
                          onChange={(e) => {
                            const v = e.target.value ? Number(e.target.value) : null;
                            setScoreDrafts((prev) => ({
                              ...prev,
                              [r.id]: { ...(prev[r.id] ?? emptyScores()), [f.key]: v },
                            }));
                          }}
                          className="bg-slate-950 border border-slate-700 text-xs text-white rounded px-1.5 py-1"
                        >
                          <option value="">—</option>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}

                    <textarea
                      value={notesDrafts[r.id] ?? ''}
                      onChange={(e) =>
                        setNotesDrafts((prev) => ({ ...prev, [r.id]: e.target.value }))
                      }
                      placeholder="Notes…"
                      rows={2}
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-white placeholder-slate-600"
                    />

                    <button
                      type="button"
                      onClick={() => saveScores(r.id)}
                      disabled={savingScoreId === r.id}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 bg-slate-800 hover:bg-slate-700 text-xs text-white rounded-lg disabled:opacity-50"
                    >
                      {savingScoreId === r.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Star className="w-3 h-3" />
                      )}
                      Save scores
                    </button>

                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {TIER_ASSIGN_OPTIONS.map((t) => {
                        const key = `${r.model_id}:${t.key}`;
                        return (
                          <button
                            key={t.key}
                            type="button"
                            disabled={assigningKey === key || r.status !== 'completed'}
                            onClick={() => assignToTier(r.model_id, t.key)}
                            className="flex-1 min-w-[30%] inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium rounded-lg bg-amber-900/30 text-amber-300 hover:bg-amber-900/50 disabled:opacity-40"
                          >
                            {assigningKey === key ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              `Set as ${t.label}`
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-slate-950 border border-slate-800 rounded-2xl p-5 shadow-2xl">
            <h3 className="text-white font-semibold mb-2">Confirm model test</h3>
            <p className="text-sm text-slate-400 mb-4">
              This will run {selectedModelIds.length} fal generation
              {selectedModelIds.length !== 1 ? 's' : ''} against your uploaded image. Estimated
              cost:{' '}
              <span className="text-amber-400 font-semibold">
                {estimatedCost != null ? `$${estimatedCost.toFixed(2)}` : 'unknown'}
              </span>
              . No customer credits are charged.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleStart}
                disabled={starting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
              >
                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Confirm & run
              </button>
            </div>
            {estimatedCost == null && (
              <p className="mt-3 text-xs text-amber-400/80 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Cost estimate unavailable — proceed carefully.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
